import { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { getOptimizationData } from '../utils/chartAPI';

export default function useOptimizationData({
  site,
  msSessionInfo,
  startDate,
  endDate,
  inView,
  refreshKey,
}) {
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const hasFetched = useRef(false);

  useEffect(() => {
    hasFetched.current = false;
  }, [refreshKey, startDate?.valueOf?.(), endDate?.valueOf?.()]);

  useEffect(() => {
    if (!inView || !site?.id || !msSessionInfo) return;
    if (!startDate || !endDate) {
      setLoading(false);
      setSeries([]);
      return;
    }
    if (refreshKey === undefined && hasFetched.current) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const start = dayjs(startDate);
        let end = dayjs(endDate);
        if (!end.isAfter(start)) end = start.add(1, 'minute');

        const [vderData, dlmData] = await Promise.all([
          getOptimizationData(msSessionInfo, site, start, end, null, 'VDER'),
          getOptimizationData(msSessionInfo, site, start, end, null, 'DLM'),
        ]);

        const vderPower = vderData?.power ?? [];
        const dlmPower = dlmData?.power ?? [];
        const vderSoc = vderData?.soc ?? [];
        const dlmSoc = dlmData?.soc ?? [];

        const tsSet = new Set([
          ...vderPower.map(p => p.time),
          ...dlmPower.map(p => p.time),
          ...vderSoc.map(p => p.time),
          ...dlmSoc.map(p => p.time),
        ]);
        const tsSorted = Array.from(tsSet).sort(
          (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
        );

        const mapFrom = (arr) => new Map(arr.map(p => [p.time, p.value]));
        const vderPMap = mapFrom(vderPower);
        const dlmPMap = mapFrom(dlmPower);
        const socAgg = new Map();
        vderSoc.forEach(d => socAgg.set(d.time, (socAgg.get(d.time) ?? 0) + d.value));
        dlmSoc.forEach(d => socAgg.set(d.time, (socAgg.get(d.time) ?? 0) + d.value));


        const toPts = (getterMap) =>
          tsSorted.map(t => ({
            x: dayjs(t).valueOf(),
            y: getterMap.has(t) ? Number(getterMap.get(t)) : null,
          }));
        const vderPts = toPts(vderPMap);
        const dlmPts = toPts(dlmPMap);
        const socPts = tsSorted.map(t => ({
          x: dayjs(t).valueOf(),
          y: socAgg.has(t) ? Number(socAgg.get(t)) : null,
        }));

        setSeries([
          { name: 'VDER Power', type: 'column', data: vderPts, yAxis: 0 },
          { name: 'DLM Power', type: 'column', data: dlmPts, yAxis: 0 },
          { name: 'SOC', type: 'line', data: socPts, yAxis: 1 },
        ]);

        hasFetched.current = true;
      } catch (err) {
        console.error('Optimization fetch error:', err);
        setSeries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    inView,
    site?.id,
    msSessionInfo,
    startDate?.valueOf?.(),
    endDate?.valueOf?.(),
    refreshKey,
  ]);

  return { loading, series };
}
